import * as Yup from 'yup';

export const userSchema = Yup.object().shape({
  email: Yup.string().trim().email('Enter a valid email address').required('Email is required'),
  role: Yup.number().moreThan(0, 'Role is required').required('Role is required'),
});

export type UserFormValues = Yup.InferType<typeof userSchema>;

const normalizeEmail = (email?: string | null) => email?.trim().toLowerCase();

export const inviteUserSchema = Yup.object().shape({
  users: Yup.array()
    .of(userSchema)
    .min(1, 'At least one user must be invited')
    .test('unique-emails', function (users) {
      if (!users?.length) {
        return true;
      }

      const duplicateEmailErrors = new Map<string, Yup.ValidationError[]>();

      users.forEach((user, index) => {
        const normalizedEmail = normalizeEmail(user?.email);

        if (!normalizedEmail) {
          return;
        }

        const errors = duplicateEmailErrors.get(normalizedEmail) ?? [];

        errors.push(
          this.createError({
            path: `${this.path}[${index}].email`,
            message: 'Email addresses must be unique',
          })
        );

        duplicateEmailErrors.set(normalizedEmail, errors);
      });

      const validationErrors = Array.from(duplicateEmailErrors.values())
        .filter(errors => errors.length > 1)
        .flat();

      if (validationErrors.length > 0) {
        throw new Yup.ValidationError(validationErrors);
      }

      return true;
    })
    .required('Users are required'),
});

export type InviteUserFormValues = Yup.InferType<typeof inviteUserSchema>;

export const updateUserProfileSchema = Yup.object().shape({
  first_name: Yup.string()
    .required('First name is required')
    .max(50, 'First name cannot exceed 50 characters'),
  last_name: Yup.string()
    .required('Last name is required')
    .max(50, 'Last name cannot exceed 50 characters'),
  phone_number: Yup.string()
    .optional()
    .nonNullable('Phone number cannot be null')
    .max(15, 'Phone number cannot exceed 15 characters'),
});

export type UpdateUserProfileFormValues = Yup.InferType<typeof updateUserProfileSchema>;

export const changePasswordSchema = Yup.object().shape({
  current_password: Yup.string().required('Current password is required'),
  new_password: Yup.string()
    .required('New password is required')
    .min(8, 'New password must be at least 8 characters long')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)'
    ),
  confirm_new_password: Yup.string()
    .oneOf([Yup.ref('new_password'), null], 'Passwords must match')
    .required('Please confirm your new password'),
});

export type ChangePasswordFormValues = Yup.InferType<typeof changePasswordSchema>;
